import React from 'react';
import { Form, Input, Button, Typography, notification, Image, Divider } from 'antd';
import { useNavigate } from 'react-router-dom';
import './UserLogin.css';
import { useGetAllFromKosQuery, useLazyGetAllFromKosQuery } from '../../store/commonApiKos';
import buildQueryString from '../../utils/buildQueryString';
import VULogo from '../../assets/VULogo.png';
import KosLogo from '../../assets/KosLogo.png';
const { Title, Text } = Typography;

function Login() {
    return (
        <div className='UserLogin-container'>
            <div className='image-half' id='UserLogin-illustration-placeholder'></div>
            <div className='form-half'>
                <MainLoginForm />
            </div>
        </div>
    );
}

function MainLoginForm() {
    const [loginForm] = Form.useForm();
    const [loginTrigger, { isLoading }] = useLazyGetAllFromKosQuery();
    const [getRoleTrigger] = useLazyGetAllFromKosQuery();
    const [setRoleTrigger] = useLazyGetAllFromKosQuery();
    const navigate = useNavigate();

    const onFinish = async (values) => {
        const InvalidCred = ['Invalid credentials. Please try again.'];
        try {
            const authResponse = await loginTrigger({
                endpoint: 'authenticate',
                body: values,
                method: 'POST',
                urlBase: '/user_verification/',
            }).unwrap();
            localStorage.setItem('data', JSON.stringify(authResponse.data));
            // GET role info (no app_user_id needed)
            const roleResponse = await getRoleTrigger({
                endpoint: 'get_role',
                method: 'GET',
                urlBase: '/user_role/',
            }).unwrap();
            const firstRole = roleResponse?.roles?.[0];
            if (!firstRole) {
                // Debug: log full response if no roles found
                console.error('Role response missing roles:', roleResponse);
                setLoginInvalid(loginForm, [
                    'No roles found for user. Please contact support.',
                ]);
                return;
            }
            const role_id = firstRole.role_id;
            const user_group_id = firstRole.user_group_id;
            // POST to set role
            await setRoleTrigger({
                endpoint: 'set_role', // changed from 'get_role' to 'set_role'
                body: { role_id, user_group_id },
                method: 'POST',
                urlBase: '/user_role/',
            }).unwrap();
            navigate('/ProgramPage/');
        } catch (error) {
            // Debug: log error details
            console.error('Login error:', error);
            if (error.status === 401) {
                setLoginInvalid(loginForm, InvalidCred);
            } else if (error.data && error.data.message) {
                setLoginInvalid(loginForm, [error.data.message]);
            } else {
                setLoginInvalid(loginForm, [
                    'An error occurred. Please try again later or contact the system administrator.',
                ]);
            }
        }
    };

    return (
        <div className='UserLogin-form-container'>
            <Image src={VULogo} alt='Company Logo' preview={false} width={176} height={60} />
            <div>
                <Title
                    level={2}
                    style={{
                        marginBottom: '0',
                        fontWeight: '700',
                        marginTop: '0',
                        color: '#2D2D2D',
                        textAlign: 'center',
                    }}
                >
                    Welcome to{' '}
                </Title>
                <Title
                    level={2}
                    style={{
                        marginBottom: '0',
                        fontWeight: '700',
                        marginTop: '0',
                        color: '#2D2D2D',
                    }}
                >
                    Vijaybhoomi University!
                </Title>
                <Text
                    style={{
                        color: '#555555',
                    }}
                >
                    Enter your account details to access the course catalogue
                </Text>
            </div>
            <div className='UserLogin-form'>
                <Form
                    name='login'
                    onFinish={onFinish}
                    autoComplete='off'
                    labelAlign='left'
                    form={loginForm}
                    layout='vertical'
                    size='large'
                    requiredMark={false}
                >
                    <Form.Item
                        label={
                            <Text
                                style={{
                                    color: '#47526D',
                                    marginBottom: '0',
                                    marginTop: '0',
                                }}
                            >
                                Email Address
                            </Text>
                        }
                        name='user_name'
                        style={{
                            marginBottom: '0',
                        }}
                        rules={[{ required: true, message: 'Required' }]}
                    >
                        <Input />
                    </Form.Item>
                    <Form.Item
                        label={
                            <Text
                                style={{
                                    color: '#47526D',
                                    marginBottom: '0',
                                    padding: '0',
                                }}
                            >
                                Password
                            </Text>
                        }
                        name='secret_key'
                        rules={[
                            {
                                required: true,
                                message: 'Required',
                            },
                        ]}
                    >
                        <Input.Password />
                    </Form.Item>

                    <Button
                        type='primary'
                        htmlType='submit'
                        loading={isLoading}
                        style={{
                            width: '100%',
                        }}
                    >
                        {isLoading ? 'Logging in...' : 'Login'}
                    </Button>
                </Form>
                <Divider
                    style={{
                        borderColor: '#F0F0F0',
                        marginTop: '5px',
                        marginBottom: '5px',
                    }}
                >
                    OR
                </Divider>
                <LoginSSO />
            </div>
            <div className='powered-by'>
                <p> Powered by</p>
                <img
                    src={KosLogo}
                    alt='Company Logo'
                    style={{
                        width: '120px',
                        height: '25px',
                    }}
                />
            </div>
        </div>
    );
}

function LoginSSO() {
    const { data, isLoading, error } = useGetAllFromKosQuery({
        endpoint: 'connector/sso',
        urlBase: '/',
    });

    if (isLoading) {
        return <div>Loading...</div>;
    }
    if (error) console.error(error);

    return (
        <div className='AllSsoClients'>
            {data?.objects?.map((client) => {
                return <SingleSsoClientComponent client={client} key={client.id} />;
            })}
        </div>
    );
}

function SingleSsoClientComponent({ client }) {
    const [redirectTrigger] = useLazyGetAllFromKosQuery();

    function onClick() {
        redirectTrigger({
            endpoint: 'connector/sso_login',
            urlBase: '/',
            params: {
                id: client.id,
                redirect_url:
                    window.location.origin +
                    '/SSORedirect?' +
                    buildQueryString({
                        name: client.system_connector.name,
                    }),
            },
        })
            .unwrap()
            .then((response) => {
                if (response.sso_login.redirect_url) {
                    window.location.href = response.sso_login.redirect_url;
                } else if (response.message) {
                    notification.error({
                        message: response.message,
                        placement: 'bottomRight',
                    });
                } else {
                    console.error('No Sso Clients Found');
                }
            })
            .catch((error) => console.error(error));
    }

    return (
        <Button type='default' onClick={onClick} style={{ width: '100%', marginBottom: '10px' }}>
            <div
                style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    marginTop: '-13px',
                }}
            >
                <img
                    src={client.system_connector.logo}
                    alt={client.name}
                    style={{
                        width: '25px',
                        height: '25px',
                    }}
                />
                <p> {client.name}</p>
            </div>
        </Button>
    );
}

function setLoginInvalid(loginForm, errors) {
    loginForm.setFields([
        {
            name: 'secret_key',
            errors: errors,
        },
    ]);
}

export default Login;
